import {
  DefaultButton,
  Link,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Modal,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import { DialogFooter } from "@fluentui/react/lib/Dialog";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";

import { useState } from "react";
import { BaseTextField } from "../../../ExperimentDetails/FormComponents";
import { useEv2ExperimentParams } from "../../../../Hooks/UseCurrentExtension";
import { PipelineConfigurationPr, PullRequestChange } from "../../../../Types";
import {
  createAdoBranch,
  createAdoPullRequest,
  pushToAdoBranch,
} from "../../../../Api/ado.api";
import {
  injectWiggleAnimation,
  styles,
} from "../../../../Shared/shared.styles";
import {
  validateConfigurePipelineField,
  validateConfigurePipelineForm,
} from "./configurePipelineValidation";
import { PIPELINE_CONFIG_PR_KEY } from "../../../../Shared/shared.consts";
import useAppInsights from "../../../../Hooks/UseAppInsights";
import { OperationTracker } from "../../../../Hooks/UseAppInsights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { NavType } from "../../../../Shared/AppInsights";

interface ConfigPipelineDialogProps {
  /**
   * Callback function for when the dialog is canceled.
   */
  onCancel: () => void;
  /**
   * Callback function for when the configuration is successfully submitted.
   */
  onSuccess?: () => void;
  /**
   * Existing pipeline configuration, if any. If none provided,
   * dialog assumes a new configuration is being created.
   */
  configuration?: PipelineConfigurationPr | null;
}

/**
 * Dialog component for configuring pipeline IDs for an extension.
 * Allows users to input and submit pipeline IDs, creating or updating
 * a pull request in ADO with the configuration.
 */
const ConfigPipelineDialog: React.FC<ConfigPipelineDialogProps> = ({
  onCancel,
  onSuccess,
  configuration,
}) => {
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [startWiggle, setStartWiggle] = useState(false);
  const [singleStagePipelineId, setSingleStagePipelineId] = useState(
    configuration?.singleStagePipelineId || "",
  );
  const [unifiedPipelineId, setUnifiedPipelineId] = useState(
    configuration?.unifiedPipelineId || "",
  );
  const { extensionFolder } = useEv2ExperimentParams();
  const {
    trackNavigationEvent,
    trackOperationEvent,
    trackExceptionEvent,
    createOperationTracker,
  } = useAppInsights("ConfigPipelineDialog");
  const extensionRoot = extensionFolder?.rootExtension;
  const isUpdate = Boolean(configuration);

  const onSubmit = async (tracker: OperationTracker) => {
    const commitModifier = isUpdate ? "Update" : "Add";
    const { sourceRefName } = configuration || {};
    const branchName =
      sourceRefName || `${extensionRoot}/${PIPELINE_CONFIG_PR_KEY}`;
    const fileContent = JSON.stringify({
      singleStagePipelineId: singleStagePipelineId?.trim() || null,
      unifiedPipelineId: unifiedPipelineId?.trim() || null,
    });
    const itemPath = `Extensions/${extensionRoot}/.pipelines/pipelineIds.json`;
    const changes: PullRequestChange[] = [
      {
        changeType: isUpdate ? "edit" : "add",
        itemPath,
        newContent: fileContent,
        contentType: "rawtext" as const,
      },
    ];
    const commitMessage = `${commitModifier} pipelineIds.json for ${extensionRoot}`;
    const prTitle = `${commitModifier} pipelineIds.json for ${extensionRoot}`;
    const prDescription = `This PR ${commitModifier.toLowerCase()}s the pipelineIds.json file to configure pipelines for the ${extensionRoot} extension.`;
    try {
      if (!isUpdate) {
        await createAdoBranch(branchName, "main");
        tracker.step("BranchCreated", { branchName });
      }
      await pushToAdoBranch({ branchName, commitMessage, changes });
      tracker.step("FilesPushed", { branchName, commitMessage });
      if (!isUpdate) {
        await createAdoPullRequest({
          sourceBranch: branchName,
          targetBranch: "main",
          title: prTitle,
          description: prDescription,
        });
        tracker.step("PullRequestCreated", { branchName });
      }
    } catch (error) {
      console.error("Error creating pull request:", error);
      trackExceptionEvent(
        error instanceof Error ? error : new Error(String(error)),
        SeverityLevel.Error,
        {
          function: "onSubmit",
          properties: {
            branchName,
            commitMessage,
            changes,
            prDetails: {
              sourceBranch: branchName,
              targetBranch: "main",
              title: prTitle,
              description: prDescription,
            },
          },
        },
      );
      throw error;
    }
  };

  const onClose = () => {
    onCancel();
  };

  const handleSubmit = async () => {
    const configuration = { singleStagePipelineId, unifiedPipelineId };
    const isValid = validateConfigurePipelineForm(configuration, setFormErrors);

    if (!isValid) {
      setStartWiggle(true);
      setTimeout(() => setStartWiggle(false), 600);
      return;
    }
    setLoading(true);

    const tracker = createOperationTracker("ConfigurePipeline", "handleSubmit");
    tracker.start({
      configuration,
      isUpdate: isUpdate,
    });

    try {
      await onSubmit(tracker);
      tracker.succeed();
      setLoading(false);
      if (onSuccess) {
        onSuccess();
      }
      onClose(); // Close the dialog on success
    } catch (err) {
      console.error("Error during submission:", err);
      tracker.fail(err instanceof Error ? err.message : String(err));
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onDismiss={onClose}
      containerClassName={localStyles.modal}
    >
      {loading && (
        <div className={styles.spinnerOverlay}>
          <Spinner
            size={SpinnerSize.large}
            label={
              isUpdate
                ? "Updating Configuration..."
                : "Creating Configuration..."
            }
            labelPosition="bottom"
          />
        </div>
      )}

      <Stack tokens={{ childrenGap: 15 }}>
        {/* Header */}
        <Text variant="large">{`${
          isUpdate ? "Update" : "Configure"
        } Pipeline IDs`}</Text>
        {/* Body */}
        {formErrors["form"] && (
          <MessageBar messageBarType={MessageBarType.error}>
            {formErrors["form"].join(", ")}
          </MessageBar>
        )}
        {error && (
          <MessageBar messageBarType={MessageBarType.error}>
            Error fetching pipelines: {error}
          </MessageBar>
        )}
        <Accordion
          collapsible
          onClick={() => {
            trackOperationEvent({
              operation: "AccordionToggle",
              properties: {
                accordionHeader: "How to locate pipeline IDs",
                accordionItem: "instructions",
              },
            });
          }}
        >
          <AccordionItem value="instructions">
            <AccordionHeader>
              <Text variant="mediumPlus">How to locate pipeline IDs</Text>
            </AccordionHeader>
            <AccordionPanel>
              <ol>
                {[
                  {
                    title: "Locate Pipeline in ADO",
                    content: (
                      <>
                        <Text>
                          Go to the{" "}
                          <Link
                            href="https://dev.azure.com/msazure/One/_build"
                            target="_blank"
                            onClick={() => {
                              trackNavigationEvent({
                                component: "ConfigPipelineDialog",
                                destination:
                                  "https://dev.azure.com/msazure/One/_build",
                                navType: NavType.External,
                              });
                            }}
                          >
                            ADO pipelines page
                          </Link>{" "}
                          and search for your experiment pipeline(s).
                        </Text>
                      </>
                    ),
                  },
                  {
                    title: "Copy Pipeline ID",
                    content: (
                      <>
                        <Text>
                          Open the pipeline and copy the <i>definitionId</i>{" "}
                          from the URL. Example: <b>1234</b> from{" "}
                          <i>
                            https://dev.azure.com/msazure/One/_build?definitionId=1234
                          </i>
                        </Text>
                      </>
                    ),
                  },
                  {
                    title: "Enter Pipeline ID",
                    content: (
                      <>
                        <Text>
                          Paste the pipeline ID into the field(s) below. Repeat
                          for each pipeline if needed.
                        </Text>
                      </>
                    ),
                  },
                ].map((step) => (
                  <li key={step.title} style={{ marginBottom: "10px" }}>
                    {step.content}
                  </li>
                ))}
              </ol>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
        <BaseTextField
          label="Single Stage Id"
          value={singleStagePipelineId}
          fieldKey="singleStagePipelineId"
          errorMessage={formErrors["singleStagePipelineId"]}
          onChange={(_, value) => {
            validateConfigurePipelineField(
              "singleStagePipelineId",
              value,
              setFormErrors,
            );
            setSingleStagePipelineId(value ?? "");
          }}
          isEdit={!loading}
        />
        <BaseTextField
          label="Unified Pipeline Id"
          value={unifiedPipelineId}
          fieldKey="unifiedPipelineId"
          errorMessage={formErrors["unifiedPipelineId"]}
          onChange={(_, value) => {
            validateConfigurePipelineField(
              "unifiedPipelineId",
              value,
              setFormErrors,
            );
            setUnifiedPipelineId(value ?? "");
          }}
          isEdit={!loading}
        />
        <MessageBar messageBarType={MessageBarType.info} isMultiline={true}>
          On submit will {isUpdate ? "update the" : "create a"} pull request
          with your configuration under the extension's root, {extensionRoot}
          /.pipelines.
        </MessageBar>
      </Stack>

      <DialogFooter>
        <Stack horizontalAlign="end">
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton
              onClick={handleSubmit}
              text="Submit"
              disabled={loading}
              style={startWiggle ? injectWiggleAnimation : undefined}
            />
            <DefaultButton onClick={onClose} text="Cancel" disabled={loading} />
          </Stack>
        </Stack>
      </DialogFooter>
    </Modal>
  );
};

const localStyles = mergeStyleSets({
  modal: {
    maxWidth: 600,
    padding: 15,
    "& .ms-Modal-scrollableContent": {
      overflowY: "unset",
    },
  },
});

export default ConfigPipelineDialog;
